import * as Sentry from "@sentry/react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * The app's generic "something broke, reload" screen. Shared so the two
 * top-level error surfaces stay visually identical: the React error boundary in
 * `main.tsx` (render-time crashes) and the router's `defaultErrorComponent`
 * (errors thrown inside route loaders/components).
 */
export function AppErrorFallback() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-muted-foreground text-sm">
        Something went wrong. Please reload the app.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md border px-4 py-2 text-sm"
      >
        Reload
      </button>
    </div>
  );
}

/**
 * Router `defaultErrorComponent`. Unlike a React error boundary, the router
 * *catches* errors thrown in a route's loader/component and renders this in
 * place — so they never reach the `Sentry.ErrorBoundary` in `main.tsx` and were
 * previously invisible to Sentry (see issue #180). Capture them explicitly here.
 *
 * A stale-deploy module-load error normally self-heals via the one-shot reload
 * in `reload-on-stale-deploy.ts` before it can reach the router; if one lands
 * here it means that reload already ran and didn't fix it, so it's a genuinely
 * broken deploy worth a Sentry issue.
 */
export function RootErrorComponent({ error }: ErrorComponentProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return <AppErrorFallback />;
}
